import React from 'react';
import { useTranslation } from 'react-i18next';
import ModalV2 from 'common/components/Modal/ModalV2';
import { LoadingButton } from 'common/components/Form/Button';
import { SecondaryButton } from 'common/components/Form/SecondaryButton';
import { Text } from 'common/styledComponents/typography';
import { getDataExportErrorMessages } from 'common/queries/dataExport';
import { IssueDataExportTokenPayload } from 'common/types/dataExport';
import { describedBy, isTokenNameTooLong, TOKEN_NAME_MAX_LENGTH } from 'modules/settings/components/DataExport/utils';
import {
  ErrorBanner,
  Field,
  FieldError,
  FieldHint,
  FieldLabel,
  ModalActions,
  ModalBody,
  TextField,
} from 'modules/settings/components/DataExport/styled';

interface Props {
  isSubmitting: boolean;
  /** Resolves when the token was issued (the parent then shows it once), rejects with the request error. */
  onSubmit: (payload: IssueDataExportTokenPayload) => Promise<unknown>;
  onClose: () => void;
}

/** Asks for an optional token name before issuing. Mounted only while open; the input survives a rejected submit. */
const IssueTokenModal: React.FC<Props> = ({ isSubmitting, onSubmit, onClose }) => {
  const { t } = useTranslation();
  const [name, setName] = React.useState('');
  const [isTooLong, setIsTooLong] = React.useState(false);
  const [serverErrors, setServerErrors] = React.useState<string[]>([]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const tooLong = isTokenNameTooLong(name);
    setIsTooLong(tooLong);
    setServerErrors([]);
    if (tooLong) return;

    const trimmedName = name.trim();
    onSubmit(trimmedName ? { name: trimmedName } : {}).catch(error => {
      const messages = getDataExportErrorMessages(error);
      setServerErrors(messages.length ? messages : [t('dataExport.tokens.issueFailed')]);
    });
  };

  return (
    <ModalV2
      isOpen
      appearance='card'
      modalId='data-export-issue-token-modal'
      title={t('dataExport.tokens.issueModal.title')}
      clickOutsideClose={!isSubmitting}
      onClose={onClose}
      data-testid='issue-token-modal'
    >
      <form onSubmit={handleSubmit} noValidate>
        <ModalBody>
          <Text variant='body2' color='black50'>
            {t('dataExport.tokens.issueModal.intro')}
          </Text>
          <Field>
            <FieldLabel htmlFor='data-export-token-name'>{t('dataExport.tokens.issueModal.nameLabel')}</FieldLabel>
            <TextField
              id='data-export-token-name'
              name='name'
              type='text'
              placeholder={t('dataExport.tokens.issueModal.namePlaceholder')}
              value={name}
              onChange={e => setName(e.target.value)}
              maxLength={TOKEN_NAME_MAX_LENGTH}
              aria-invalid={isTooLong}
              aria-describedby={describedBy('data-export-token-name-hint', isTooLong && 'data-export-token-name-error')}
              autoFocus
            />
            <FieldHint id='data-export-token-name-hint'>
              {t('dataExport.tokens.issueModal.nameHint', { count: TOKEN_NAME_MAX_LENGTH })}
            </FieldHint>
            {isTooLong && (
              <FieldError id='data-export-token-name-error'>
                {t('dataExport.tokens.issueModal.nameTooLong', { count: TOKEN_NAME_MAX_LENGTH })}
              </FieldError>
            )}
          </Field>
          {serverErrors.length > 0 && (
            <ErrorBanner role='alert'>
              {serverErrors.map(message => (
                <p key={message}>{message}</p>
              ))}
            </ErrorBanner>
          )}
          <ModalActions>
            <SecondaryButton type='button' color='white' isButtonV2 disabled={isSubmitting} onClick={onClose}>
              {t('common.cancel')}
            </SecondaryButton>
            <LoadingButton type='submit' color='lightPurple' pill={false} isButtonV2 loading={isSubmitting} disabled={isSubmitting}>
              {t('dataExport.tokens.issueModal.submit')}
            </LoadingButton>
          </ModalActions>
        </ModalBody>
      </form>
    </ModalV2>
  );
};

export default IssueTokenModal;
